import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Trophy, Sparkles } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import PickBadge from '../components/common/PickBadge';
import GradingLoader from '../components/common/GradingLoader';
import { League } from '../types';
import { v1Api } from '../services/api';

const Dashboard: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingMockLeague, setIsCreatingMockLeague] = useState(false);

  const loadLeagues = useCallback(async () => {
    if (leagues.length === 0) {
      setIsLoading(true);
    }
    
    try {
      // Simulate initial load delay for better UX
      const isInitialLoad = leagues.length === 0;
      
      // Add a minimum loading time to prevent flash of loading state
      const [data] = await Promise.all([
        v1Api.leagues.getAll(),
        new Promise(resolve => setTimeout(resolve, isInitialLoad ? 800 : 300))
      ]);
      
      setLeagues(data);
    } catch (error) {
      console.error('Failed to load leagues:', error);
    } finally {
      setIsLoading(false);
    }
  }, [leagues.length]);

  useEffect(() => {
    loadLeagues();
  }, [loadLeagues]);


  const createMockLeague = async () => {
    setIsCreatingMockLeague(true);
    try {
      // For now, just show message that mock league creation is not implemented in v1
      console.log('Mock league creation not implemented in v1 API yet');
      await loadLeagues(); // Refresh the list
    } catch (error) {
      console.error('Failed to create mock league:', error);
    } finally {
      setIsCreatingMockLeague(false);
    }
  };

  // Show skeleton for initial load or when no data is available yet
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-8 animate-fade-in">
        
        {/* Hero Section with Logo */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="/draftedge_logo.png" 
              alt="DraftEdge"
              className="h-12 w-auto object-contain sm:h-16 lg:h-20"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4 tracking-tight">
            Your season, graded.
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Pick by pick. Week by week. No mercy.
          </p>
        </div>
        
        {/* Premium Leagues Section */}
        <div>
        <div className="mb-8">
          <div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">Your Leagues</h2>
            <p className="text-text-secondary">Find out who drafted best, who benched worst, and who had no business making the playoffs.</p>
          </div>
        </div>

        {leagues.length === 0 ? (
          <EmptyState onCreateMockLeague={createMockLeague} isLoading={isCreatingMockLeague} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {leagues.slice(0, 6).map((league, index) => (
              <LeagueCard 
                key={league.id} 
                league={league} 
                className="animate-scale-in"
                style={{ animationDelay: `${index * 100}ms` }}
              />
            ))}
          </div>
        )}
      </div>

      </div>
    </div>
  );
};

// Enhanced Components
const LeagueCard: React.FC<{ 
  league: League; 
  className?: string;
  style?: React.CSSProperties;
}> = ({ league, className, style }) => (
  <Link to={`/leagues/espn/${league.espn_league_id}`}>
    <Card variant="glass" hoverable className={`p-6 group ${className}`} style={style}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary truncate group-hover:text-accent-primary transition-colors duration-200">
            {league.name}
          </h3>
          <p className="text-text-secondary text-sm mt-1">
            {league.team_count || league.total_teams} teams • {league.season}
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-text-tertiary group-hover:text-accent-primary group-hover:translate-x-1 transition-all duration-200" />
      </div>
      
      <div className="flex items-center gap-2 mb-4">
        {(league.summary?.total_picks && league.summary.total_picks > 0) || league.draft_complete ? (
          <PickBadge variant="done" label="Graded" size="md" />
        ) : (
          <PickBadge variant="pending" label="Pending" size="md" />
        )}
        {(league.summary?.total_picks || league.total_picks) && (
          <span className="text-text-tertiary text-xs px-2 py-1 bg-white/5 rounded font-mono">
            {league.summary?.total_picks || league.total_picks} picks
          </span>
        )}
      </div>

      <p className="text-sm text-accent-primary font-medium group-hover:underline underline-offset-4">
        See the grades
      </p>
    </Card>
  </Link>
);

const EmptyState: React.FC<{
  onCreateMockLeague: () => void;
  isLoading: boolean;
}> = ({ onCreateMockLeague, isLoading }) => (
  <Card variant="glass" className="p-12 text-center">
    <div className="max-w-md mx-auto">
      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-primary rounded-full flex items-center justify-center">
        <Trophy className="w-10 h-10 text-background-primary" />
      </div>
      <h3 className="text-xl font-semibold text-text-primary mb-4">Nobody's been graded yet.</h3>
      <p className="text-text-secondary mb-8">
        Import a league and find out who in your group chat actually knows what they're doing. Spoiler: it's fewer people than they think.
      </p>
      <Button
        variant="premium"
        size="lg"
        icon={<Sparkles className="w-5 h-5" />}
        onClick={onCreateMockLeague}
        isLoading={isLoading}
        className="animate-pulse-slow"
      >
        Create Your First League
      </Button>
    </div>
  </Card>
);

const DashboardSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background-primary">
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6 animate-fade-in">
      <GradingLoader label="Grading your leagues…" />

      {/* League Cards Skeleton — matches the real card grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="shimmer-loading h-40 w-full rounded-xl border border-fieldline"
            style={{ animationDelay: `${i * 100}ms` }}
          ></div>
        ))}
      </div>
    </div>
  </div>
);

export default Dashboard; 